import { Field, reduxForm } from "redux-form";
import { useState } from "react";
import Dropzone from "react-dropzone-uploader";
import { toastr } from "react-redux-toastr";
import { Button, Input } from "expano-components";
import { Modal, Select } from "components";
import { client, getApiErrorMessage } from "utils/api";
import { ReactComponent as DropzoneIcon } from "icons/dropzone.svg";

const LANG_OPTIONS = [
  { label: "PL", value: "PL" },
  { label: "EN", value: "EN" },
  { label: "DE", value: "DE" },
];

const LangSelectField = ({ input, meta, label }) => (
  <Select
    label={label}
    meta={meta}
    options={LANG_OPTIONS}
    value={LANG_OPTIONS.find((o) => o.value === input.value) ?? null}
    handleChange={(v) => {
      input.onChange(v);
      input.onBlur();
    }}
  />
);

const dropzoneLabelStyle = {
  fontSize: "15px",
  lineHeight: "23px",
  fontWeight: 300,
  color: "#595D6E",
};

const DocumentDropzoneField = ({ input, meta, label }) => {
  const handleChangeStatus = ({ file }, status) => {
    if (status === "removed") {
      input.onChange(null);
      input.onBlur();
      return;
    }
    if (status === "done") {
      input.onChange(file);
      input.onBlur();
      return;
    }
    if (
      [
        "rejected_max_files",
        "rejected_file_type",
        "error_file_size",
        "error_validation",
      ].includes(status)
    ) {
      input.onChange(null);
      input.onBlur();
    }
  };

  return (
    <div className="m-b-10">
      {label && <label className="label">{label}</label>}
      <Dropzone
        onChangeStatus={handleChangeStatus}
        SubmitButtonComponent={null}
        maxFiles={1}
        multiple={false}
        canCancel={false}
        accept=".pdf,application/pdf"
        inputContent={() => (
          <>
            <DropzoneIcon />
            <p style={dropzoneLabelStyle}>
              Przenieś plik PDF tutaj lub kliknij, aby go dodać.
            </p>
          </>
        )}
        inputWithFilesContent="Zmień plik"
        styles={{
          dropzone: {
            width: "100%",
            maxWidth: "100%",
            minHeight: 140,
            padding: 10,
            border: "2px dashed #EBEDF2",
            borderRadius: 0,
            flexDirection: "row",
            flexFlow: "wrap",
            justifyContent: "center",
          },
          inputLabel: {
            flexDirection: "column",
            justifyContent: "center",
          },
          previewImage: {
            maxHeight: "initial",
            maxWidth: "calc(100% - 15px)",
          },
          preview: {
            padding: 10,
            border: "none",
            display: "inline-flex",
            width: "auto",
            alignItems: "flex-start",
          },
          inputLabelWithFiles: {
            margin: "auto",
            backgroundColor: "#44B2F1",
            color: "#fff",
            borderRadius: "5px",
          },
        }}
        disabled={(files) =>
          files.some((f) =>
            ["preparing", "getting_upload_params", "uploading"].includes(
              f.meta.status,
            ),
          )
        }
      />
      {meta.error && (meta.touched || meta.submitFailed) && (
        <span className="text-danger">{meta.error}</span>
      )}
    </div>
  );
};

const validate = (values) => {
  const errors = {};
  if (!values.name?.trim()) {
    errors.name = "Pole wymagane";
  }
  if (!values.lang) {
    errors.lang = "Wybierz wersję językową";
  }
  if (!values.file) {
    errors.file = "Wybierz plik PDF";
  } else if (!values.file.name?.toLowerCase().endsWith(".pdf")) {
    errors.file = "Dozwolony jest tylko plik PDF";
  }
  return errors;
};

const AddDocumentModal = ({
  product_group_id,
  refetch,
  handleClose,
  handleSubmit,
  pristine,
  valid,
}) => {
  const [is_submitting, setSubmitting] = useState(false);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("name", values.name.trim());
      body.append("lang", values.lang);
      body.append("product_group_id", product_group_id);
      body.append("file", values.file);
      await client.post("/document_product_groups", body);
      toastr.success("Sukces", "Dodano dokument");
      refetch();
      handleClose();
    } catch (error) {
      toastr.error("Błąd", getApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      header={{ title: "Dodaj dokument" }}
      handleClose={handleClose}
      footer={
        <>
          <Button type="cancel" onClick={handleClose} />
          <Button
            type="add-green"
            disabled={pristine || !valid || is_submitting}
            text={is_submitting ? "Proszę czekać…" : "Dodaj"}
            onClick={handleSubmit(onSubmit)}
          />
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <input type="submit" style={{ display: "none" }} />
        <Field label="Nazwa dokumentu" name="name" component={Input} />
        <Field
          label="Wersja językowa"
          name="lang"
          component={LangSelectField}
        />
        <Field name="file" label="Plik PDF" component={DocumentDropzoneField} />
      </form>
    </Modal>
  );
};

export default reduxForm({
  form: "create-document-product-group",
  validate,
  enableReinitialize: true,
  initialValues: { lang: "PL" },
})(AddDocumentModal);
